import { PropTypes } from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  Flex,
  Form,
  InputNumber,
  message,
  Popconfirm,
  Table,
  Tooltip,
  Typography,
  Tag,
} from 'antd';
import { formatDate } from '../../utils/date';
import { APP_TERMS } from '../../app-terms';
import { useUserLocale } from '../../hooks/useLocale';
import { useInvoiceServiceRestore } from '../../hooks/invoice/useInvoiceServiceRestore';
import { useInvoiceServiceDelete } from '../../hooks/invoice/useInvoiceServiceDelete';
import StrikeThroughText from './StrikeThroughText';

const { Text } = Typography;

export const EditableContext = React.createContext(null);
export const EditableRow = ({ ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};
EditableRow.propTypes = {
  index: PropTypes.number,
};
export const EditableCell = ({
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);
  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };
  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({
        ...record,
        ...values,
      });
    } catch (errInfo) {
      handleSave({
        ...record,
        [dataIndex]: 0,
      });
    }
  };
  let childNode = children;
  if (editable) {
    childNode =
      editing && !record.isDeleted ? (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: dataIndex === 'rushFee' ? false : true,
              message: `required.`,
              validator: (_, value) => {
                if (dataIndex === 'rushFee') return Promise.resolve();

                if (!value) {
                  return Promise.reject('invalid');
                }
                return Promise.resolve();
              },
            },
          ]}
          initialValue={record[dataIndex]}
        >
          <InputNumber ref={inputRef} onPressEnter={save} onBlur={save} />
        </Form.Item>
      ) : record?.isDeleted ? (
        <StrikeThroughText strikeThrough value={children} />
      ) : (
        <div
          className="editable-cell-value-wrap"
          style={{
            paddingInlineEnd: 24,
          }}
          onClick={toggleEdit}
        >
          {children}
        </div>
      );
  }
  return <td {...restProps}>{childNode}</td>;
};
EditableCell.propTypes = {
  title: PropTypes.string,
  editable: PropTypes.bool,
  children: PropTypes.node,
  dataIndex: PropTypes.string,
  record: PropTypes.object,
  handleSave: PropTypes.func,
};
const EditInvoiceServicesForm = ({ onChange, value }) => {
  const [dataSource, setDataSource] = useState(value);
  const userLocale = useUserLocale();
  const {
    loading: restoreLoading,
    recordId: restoreRecordId,
    restoreInvoiceService,
  } = useInvoiceServiceRestore();

  const {
    loading: deleteLoading,
    recordId: deleteRecordId,
    deleteInvoiceService,
  } = useInvoiceServiceDelete();

  const handleDelete = async (icsId) => {
    // const activeServices = dataSource?.filter((service) => !service?.isDeleted);
    // if (activeServices?.length === 1) {
    //   message.error('Cannot delete, atleast one case service must be active!');
    //   return;
    // }
    try {
      await deleteInvoiceService(icsId);
      const newData = dataSource.map((item) => {
        if (item.icsId == icsId) {
          return {
            ...item,
            isDeleted: true,
          };
        }
        return item;
      });
      setDataSource(newData);
      onChange?.(newData);
      message.success('Invoice service deleted successfully!');
    } catch (error) {
      console.log('error on service delete: ', error);
    }
  };
  const handleRestore = async (icsId) => {
    try {
      await restoreInvoiceService(icsId);
      const newData = dataSource.map((item) => {
        if (item.icsId == icsId) {
          return {
            ...item,
            isDeleted: false,
          };
        }
        return item;
      });
      setDataSource(newData);
      onChange?.(newData);
      message.success('Invoice service restored successfully!');
    } catch (error) {
      console.log('error on service restore: ', error);
    }
  };
  const defaultColumns = [
    {
      title: 'Sl No.',
      dataIndex: 'index',
      width: 80,
      render: (_, record, index) => {
        return (
          <StrikeThroughText
            strikeThrough={record?.isDeleted}
            value={index + 1}
          />
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) =>
        dataSource.length >= 1 ? (
          <Flex justify="center">
            {record?.isDeleted ? (
              <Tooltip title="Restore Case Service">
                <Popconfirm
                  title="Sure to restore?"
                  onConfirm={() => handleRestore(record.icsId)}
                >
                  <Button
                    type="primary"
                    loading={
                      restoreLoading && record?.icsId === restoreRecordId
                    }
                    icon={<i className="fa fa-rotate-left" />}
                  />
                </Popconfirm>
              </Tooltip>
            ) : (
              <Tooltip title="Delete Case Service">
                <Popconfirm
                  title="Sure to delete?"
                  onConfirm={() => handleDelete(record.icsId)}
                >
                  <Button
                    type="primary"
                    danger
                    loading={deleteLoading && record?.icsId === deleteRecordId}
                    icon={<i className="fa fa-trash" />}
                  />
                </Popconfirm>
              </Tooltip>
            )}
          </Flex>
        ) : null,
    },
    {
      title: 'Case ID',
      dataIndex: 'caseId',
      width: 100,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        return (
          <Tooltip title={text} placement="topLeft">
            <span>
              <StrikeThroughText strikeThrough={record?.isDeleted} value={text} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Patient Name',
      dataIndex: 'casePatientName',
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        return (
          <Tooltip title={text} placement="topLeft">
            <span>
              <StrikeThroughText strikeThrough={record?.isDeleted} value={text} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Case Date',
      dataIndex: 'caseCreatedAt',
      width: 120,
      render: (text, record) => {
        const value = formatDate(text, APP_TERMS.date_1_format, userLocale);
        return (
          <StrikeThroughText strikeThrough={record?.isDeleted} value={value} />
        );
      },
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentStatus',
      width: 140,
      render: (value, record) => {
        const isPaid = !!value;
        const color = isPaid ? 'green' : 'red';
        const label = isPaid ? 'PAID' : 'UN-PAID';
        return (
          <Tag color={color}>
            <StrikeThroughText
              strikeThrough={record?.isDeleted}
              value={label}
            />
          </Tag>
        );
      },
    },
    {
      title: 'Unit Price($)',
      dataIndex: 'amount',
      editable: true,
      align: 'right',
      width: 140,
    },
    {
      title: 'Rush Fee($)',
      dataIndex: 'rushFee',
      editable: true,
      align: 'right',
      width: 140,
    },
  ];
  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.icsId === item.icsId);
    if (index === -1) return;

    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
    onChange?.(newData);
  };
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };
  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });
  return (
    <div>
      <Table
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content', y: 1000 }}
        tableLayout="fixed"
        rowKey={(record) => record?.icsId}
        summary={(pageData) => {
          let totalAmount = 0;
          let totalRushFee = 0;

          pageData.forEach(({ amount, rushFee, isDeleted }) => {
            if (!isDeleted) {
              totalAmount += amount || 0;
              totalRushFee += rushFee || 0;
            }
          });

          return (
            <Table.Summary.Row
              className="bg-light"
              style={{ fontWeight: 'bolder' }}
            >
              <Table.Summary.Cell index={0} colSpan={7}>
                Total
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} colSpan={1} align="right">
                <Text type="danger">{totalAmount + totalRushFee}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </div>
  );
};
EditInvoiceServicesForm.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.array,
};
export default EditInvoiceServicesForm;
